import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/********************************************************************************
 * Copyright (c) 2020 TypeFox and others
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License v. 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 ********************************************************************************/
import { useContext, useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Dialog, DialogContent, DialogContentText, Link } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { isError } from '../../../extension-registry-types';
import { SanitizedMarkdown } from '../../../components/sanitized-markdown';
import { Timestamp } from '../../../components/timestamp';
import { ButtonWithProgress } from '../../../components/button-with-progress';
import { createAbsoluteURL } from '../../../utils';
import { MainContext } from '../../../context';
import { cardSurface } from '../../../components/page-primitives';
import { IconTile } from '../settings/settings-primitives';
import CircularProgress from '@mui/material/CircularProgress';
export const UserPublisherAgreement = props => {
    const { service, pageSettings, updateUser, handleError } = useContext(MainContext);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [working, setWorking] = useState(false);
    const [agreementText, setAgreementText] = useState('');
    const abortController = useRef(new AbortController());
    useEffect(() => {
        return () => {
            abortController.current.abort();
        };
    }, []);
    useEffect(() => {
        if (dialogOpen) {
            onDialogOpened();
        }
    }, [dialogOpen]);
    const closePublisherAgreement = () => {
        setDialogOpen(false);
    };
    const signPublisherAgreement = async () => {
        try {
            setWorking(true);
            const result = await service.signPublisherAgreement(abortController.current);
            if (isError(result)) {
                throw result;
            }
            updateUser();
            setDialogOpen(false);
        }
        catch (err) {
            if (!err.code) {
                Object.assign(err, { code: 'publisher-agreement-problem' });
            }
            handleError(err);
        }
        finally {
            setWorking(false);
        }
    };
    const openPublisherAgreement = () => {
        if (!pageSettings.urls.publisherAgreement) {
            handleError({ error: 'Publisher agreement text is not available.' });
        }
        else {
            setDialogOpen(true);
        }
    };
    const onDialogOpened = async () => {
        const agreementURL = pageSettings.urls.publisherAgreement;
        if (agreementURL) {
            try {
                const agreementMd = await service.getStaticContent(abortController.current, agreementURL);
                setAgreementText(agreementMd);
            }
            catch (err) {
                handleError(err);
            }
        }
        else {
            setAgreementText('Publisher agreement text is not available.');
        }
    };
    const onClose = () => {
        setDialogOpen(false);
    };
    const user = props.user;
    if (!user.publisherAgreement) {
        return null;
    }
    const publisherAgreementName = pageSettings?.publisherAgreement?.name ?? '';
    const publisherAgreementSigned = user.publisherAgreement.status == 'signed';
    let title;
    let description;
    let action;
    if (publisherAgreementSigned) {
        title = 'Publisher Agreement signed';
        description = user.publisherAgreement.timestamp ? (_jsxs(_Fragment, { children: ["You signed the ", publisherAgreementName, " Publisher Agreement", ' ', _jsx(Timestamp, { value: user.publisherAgreement.timestamp }), "."] })) : (_jsxs(_Fragment, { children: ["You signed the ", publisherAgreementName, " Publisher Agreement."] }));
        action = (_jsx(Button, { onClick: openPublisherAgreement, variant: 'outlined', children: "View" }));
    }
    else if (user.additionalLogins?.find(login => login.provider === 'eclipse')) {
        title = 'Publisher Agreement required';
        description = (_jsxs(_Fragment, { children: ["You need to sign the ", publisherAgreementName, " Publisher Agreement before you can publish any extension to this registry."] }));
        action = (_jsx(Button, { onClick: openPublisherAgreement, variant: 'outlined', color: 'secondary', children: "Review & sign" }));
    }
    else {
        title = 'Publisher Agreement required';
        description = (_jsxs(_Fragment, { children: ["You need to sign the ", publisherAgreementName, " Publisher Agreement before you can publish any extension to this registry. To start the signing process, please log in with an Eclipse Foundation account."] }));
        action = (_jsx(Link, { href: createAbsoluteURL([service.serverUrl, 'oauth2', 'authorization', 'eclipse']), children: _jsx(Button, { variant: 'outlined', color: 'secondary', children: "Log in with Eclipse" }) }));
    }
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: theme => ({
                    ...cardSurface(theme),
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '0.875rem',
                    p: '1rem 1.125rem',
                    mt: '1.125rem'
                }), children: [_jsx(IconTile, { sx: { width: '2.125rem', height: '2.125rem' }, children: publisherAgreementSigned ? (_jsx(CheckIcon, { sx: { fontSize: '1.0625rem' } })) : (_jsx(DescriptionOutlinedIcon, { sx: { fontSize: '1.0625rem' } })) }), _jsxs(Box, { sx: { flex: 1, minWidth: '12.5rem' }, children: [_jsx(Typography, { sx: { fontSize: '0.875rem', fontWeight: 600 }, children: title }), _jsx(Typography, { sx: { fontSize: '0.78125rem', color: 'text.disabled', mt: '0.125rem' }, children: description })] }), action] }), _jsx(Dialog, { open: dialogOpen, onClose: onClose, maxWidth: 'xl', sx: { '& .MuiDialog-paperScrollPaper': { height: '75%', width: '100%' } }, children: _jsx(DialogContent, { children: agreementText ? (_jsxs(DialogContentText, { component: 'div', children: [_jsx(SanitizedMarkdown, { content: agreementText, sanitize: false, linkify: false }), _jsxs(Box, { display: 'flex', justifyContent: 'flex-end', children: [_jsx(Button, { onClick: closePublisherAgreement, children: "Close" }), !publisherAgreementSigned && (_jsx(ButtonWithProgress, { working: working, onClick: signPublisherAgreement, children: "Agree" }))] })] })) : (_jsx(Box, { height: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', children: _jsx(CircularProgress, { color: 'secondary' }) })) }) })] }));
};
//# sourceMappingURL=user-publisher-agreement.js.map